import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { ClassifiedsModuleConfig, ModuleFieldComponentProps } from '@/types/space-wizard'
import {
  CATEGORY_NAME_MAX,
  MAX_CATEGORIES,
  PRESET_DURATIONS,
  type ClassifiedsConfigErrors
} from './classifiedUtils'

interface Props extends ModuleFieldComponentProps<ClassifiedsModuleConfig> {
  errors?: ClassifiedsConfigErrors | null
}

/**
 * The classifieds terms (F-72 §2.3): categories, the terms an advertiser can pick,
 * whether the value shows and whether ads wait for approval.
 *
 * One component for the wizard step and the space edit (PADROES-UI §14). Only owner
 * and admin get here: the wizard and the edit dialog are theirs, and the API
 * refuses the save for anyone else (403).
 */
export const ClassifiedsSpaceFields: React.FC<Props> = ({ config, onChange, disabled = false, errors }) => {
  const { t } = useTranslation('classifieds')
  const [newName, setNewName] = React.useState('')
  const [customDuration, setCustomDuration] = React.useState('')

  const categories = config.categories ?? []
  const durations = config.allowedDurations ?? []
  const showValue = config.showValue ?? true
  const requireApproval = config.requireApproval ?? false

  /* A term already saved outside the presets (30 days is a preset, 45 is not) still shows as a chip. */
  const durationChips = Array.from(new Set([...PRESET_DURATIONS, ...durations])).sort((a, b) => a - b)

  const setCategories = (next: typeof categories) => onChange({ ...config, categories: next })

  const rename = (index: number, name: string) =>
    setCategories(categories.map((c, i) => (i === index ? { ...c, name } : c)))

  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta
    if (target < 0 || target >= categories.length) return
    const next = [...categories]
    ;[next[index], next[target]] = [next[target], next[index]]
    setCategories(next)
  }

  const addCategory = () => {
    const name = newName.trim()
    if (!name || categories.length >= MAX_CATEGORIES) return
    setCategories([...categories, { name }])
    setNewName('')
  }

  const toggleDuration = (days: number) =>
    onChange({
      ...config,
      allowedDurations: durations.includes(days) ? durations.filter((d) => d !== days) : [...durations, days]
    })

  const addCustomDuration = () => {
    const days = Number(customDuration)
    if (!Number.isInteger(days) || days < 1 || days > 365) return
    if (!durations.includes(days)) onChange({ ...config, allowedDurations: [...durations, days] })
    setCustomDuration('')
  }

  return (
    <div className="min-w-0 space-y-6" data-testid="classifieds-config">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.categories.label')}</label>
        <p className="text-xs text-gray-500">{t('config.categories.hint')}</p>
        <ul className="space-y-2">
          {categories.map((category, index) => {
            const inUse = (category.adsCount ?? 0) > 0
            return (
              <li key={category.id ?? `new-${index}`} className="flex min-w-0 items-center gap-2">
                <Input
                  value={category.name}
                  maxLength={CATEGORY_NAME_MAX}
                  disabled={disabled}
                  onChange={(e) => rename(index, e.target.value)}
                  aria-label={t('config.categories.nameLabel', { n: index + 1 })}
                  className="min-w-0 flex-1"
                />
                {inUse && (
                  <span className="hidden flex-shrink-0 text-xs text-gray-500 sm:inline">
                    {t('config.categories.inUse', { count: category.adsCount })}
                  </span>
                )}
                <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0"
                  disabled={disabled || index === 0} onClick={() => move(index, -1)}
                  aria-label={t('config.categories.moveUp')} title={t('config.categories.moveUp')}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0"
                  disabled={disabled || index === categories.length - 1} onClick={() => move(index, 1)}
                  aria-label={t('config.categories.moveDown')} title={t('config.categories.moveDown')}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                {/* A category with ads cannot go: the server refuses, and the button says why before it comes to that. */}
                <Button type="button" variant="ghost" size="sm" className="h-9 w-9 flex-shrink-0 p-0 text-red-600"
                  disabled={disabled || inUse || categories.length <= 1}
                  onClick={() => setCategories(categories.filter((_, i) => i !== index))}
                  aria-label={t('config.categories.remove')}
                  title={inUse ? t('config.categories.removeBlocked', { count: category.adsCount }) : t('config.categories.remove')}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            )
          })}
        </ul>
        <div className="flex min-w-0 items-center gap-2">
          <Input
            value={newName}
            maxLength={CATEGORY_NAME_MAX}
            disabled={disabled || categories.length >= MAX_CATEGORIES}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory() } }}
            placeholder={t('config.categories.newPlaceholder')}
            aria-label={t('config.categories.newPlaceholder')}
            className="min-w-0 flex-1"
          />
          <Button type="button" variant="outline" size="sm" className="flex-shrink-0 gap-1"
            disabled={disabled || !newName.trim() || categories.length >= MAX_CATEGORIES} onClick={addCategory}>
            <Plus className="h-4 w-4" />
            {t('config.categories.add')}
          </Button>
        </div>
        {errors?.categories && <p role="alert" className="text-xs text-red-600">{errors.categories}</p>}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.durations.label')}</label>
        <p className="text-xs text-gray-500">{t('config.durations.hint')}</p>
        <div className="flex flex-wrap gap-2">
          {durationChips.map((days) => {
            const on = durations.includes(days)
            return (
              <button
                key={days}
                type="button"
                aria-pressed={on}
                disabled={disabled}
                onClick={() => toggleDuration(days)}
                className={cn(
                  'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  on ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  disabled && 'cursor-not-allowed opacity-50'
                )}
              >
                {t('config.durations.days', { count: days })}
              </button>
            )
          })}
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={365}
            value={customDuration}
            disabled={disabled}
            onChange={(e) => setCustomDuration(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomDuration() } }}
            placeholder={t('config.durations.customPlaceholder')}
            aria-label={t('config.durations.customPlaceholder')}
            className="min-w-0 flex-1 sm:max-w-[200px]"
          />
          <Button type="button" variant="outline" size="sm" className="flex-shrink-0 gap-1"
            disabled={disabled || !customDuration} onClick={addCustomDuration}>
            <Plus className="h-4 w-4" />
            {t('config.durations.add')}
          </Button>
        </div>
        {errors?.durations && <p role="alert" className="text-xs text-red-600">{errors.durations}</p>}
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-700">{t('config.showValue.label')}</p>
          <p className="text-xs text-gray-500">{t('config.showValue.hint')}</p>
        </div>
        <Switch checked={showValue} disabled={disabled} className="data-[state=checked]:bg-primary" aria-label={t('config.showValue.label')}
          onCheckedChange={(checked) => onChange({ ...config, showValue: checked })} />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-700">{t('config.requireApproval.label')}</p>
          <p className="text-xs text-gray-500">{t('config.requireApproval.hint')}</p>
        </div>
        <Switch checked={requireApproval} disabled={disabled} className="data-[state=checked]:bg-primary" aria-label={t('config.requireApproval.label')}
          onCheckedChange={(checked) => onChange({ ...config, requireApproval: checked })} />
      </div>
    </div>
  )
}
